import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { companyInfo } from '../mock';
import { trackButtonClick } from '../utils/analytics';

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/plans', label: 'Plans' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-black/70 backdrop-blur-xl border-b border-white/10 shadow-[0_4px_24px_rgba(0,0,0,0.5)]'
          : 'bg-transparent'
      }`}
      data-testid="site-header"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" data-testid="header-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_webfiber/artifacts/zrtdvt1j_full_trimmed_transparent_customcolor.png"
              alt="SSM VISION Logo"
              className="h-14 w-auto drop-shadow-[0_0_18px_rgba(255,140,0,0.55)]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" data-testid="header-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium transition-colors relative group ${
                  isActive(link.path) ? 'text-[#FFB347]' : 'text-white/80 hover:text-[#FFB347]'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-[#FFB800] to-[#FF5C00] transition-all ${
                    isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* Call & WhatsApp Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <a href={`tel:${companyInfo.phone}`}>
              <Button
                className="bg-gradient-to-r from-[#FF8C00] to-[#FFB800] hover:from-[#FF7700] hover:to-[#FFA600] text-black font-semibold px-5 shadow-[0_4px_18px_-4px_rgba(255,140,0,0.7)]"
                onClick={() => trackButtonClick('Call Button', 'Header')}
                data-testid="header-call-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                {companyInfo.phone}
              </Button>
            </a>

            <Dialog>
              <DialogTrigger asChild>
                <Button
                  className="bg-emerald-500/20 border border-emerald-400/40 hover:bg-emerald-500/30 text-emerald-200 backdrop-blur-md font-semibold px-5"
                  onClick={() => trackButtonClick('WhatsApp Button', 'Header')}
                  data-testid="header-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md bg-[#0a0a0a] border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="text-center text-2xl">Contact Us on WhatsApp</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center space-y-4 py-4">
                  <div className="bg-white p-4 rounded-lg">
                    <img
                      src="https://customer-assets.emergentagent.com/job_webfiber/artifacts/5ymmdmrq_image.png"
                      alt="WhatsApp QR Code"
                      className="w-64 h-64"
                    />
                  </div>
                  <p className="text-center text-white/70">
                    Scan this QR code with your phone to chat with us on WhatsApp
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-base font-medium ${
                    isActive(link.path) ? 'text-[#FFB347]' : 'text-white/80'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a href={`tel:${companyInfo.phone}`}>
                <Button className="bg-gradient-to-r from-[#FF8C00] to-[#FFB800] text-black font-semibold w-full">
                  <Phone className="w-4 h-4 mr-2" />
                  {companyInfo.phone}
                </Button>
              </a>

              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-500/20 border border-emerald-400/40 hover:bg-emerald-500/30 text-emerald-200 w-full">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md bg-[#0a0a0a] border-white/10 text-white">
                  <DialogHeader>
                    <DialogTitle className="text-center text-2xl">Contact Us on WhatsApp</DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col items-center space-y-4 py-4">
                    <div className="bg-white p-4 rounded-lg">
                      <img
                        src="https://customer-assets.emergentagent.com/job_webfiber/artifacts/5ymmdmrq_image.png"
                        alt="WhatsApp QR Code"
                        className="w-64 h-64"
                      />
                    </div>
                    <p className="text-center text-white/70">
                      Scan this QR code with your phone to chat with us on WhatsApp
                    </p>
                  </div>
                </DialogContent>
              </Dialog>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
